import React, { useState } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Link, Typography, makeStyles } from 'transfix-ui/core'
import { IVendor, IVendorKeyword } from 'types/graphqlTypes'
import { EditIcon } from 'transfix-icons'
import { useImperativeQuery, useUserDetails } from 'utils/hooks'
import { DISPLAY } from 'constants/display'
import ChipsTextInput from './components/ChipsTextInput'
import { useEditKeywordMutation } from './graphql/EditKeywordsMutation'
import { useVendorOverviewQuery } from './graphql/VendorOverview'
import { IVendorOverviewFragment } from './graphql/VendorOverviewFragment'
import { VerifyKeywordDocument } from './graphql/VerifyKeyword'

export interface IKeywordsProps {
  vendorId: string
}
export interface IShipperGroup {
  shipperApplication: NonNullable<
    NonNullable<IVendorOverviewFragment['vendorShipperFacilities']>[number]['shipperFacility']
  >['shipperApplication']
  numberShipperFacilities: number
}

export const MIN_CHARACTERS = 4

const useKeywordsStyles = makeStyles(theme => ({
  table: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    margin: theme.spacing(2, 0),
  },
  tableItem: {
    padding: theme.spacing(1, 1),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    '&:nth-child(1)': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    '&:nth-last-child(-n + 1)': {
      borderBottom: 'none',
    },
  },
  editingTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 43,
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  messageContainer: {
    padding: theme.spacing(2, 0),
    '& > div:not(:first-child)': {
      marginTop: theme.spacing(1),
    },
  },
  editingButtonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    padding: theme.spacing(1),
    marginTop: 56,
  },
}))

const containsOriginalKeywords = (originalArray: string[], newArray: string[]) => {
  return originalArray.every(keyword => newArray.includes(keyword))
}

const keywordsWithId = (
  originalArray: string[],
  arrayWithIds: Pick<IVendorKeyword, 'id' | 'value'>[]
): Pick<IVendorKeyword, 'id' | 'value'>[] => {
  const keywordMapper: Record<string, string> = arrayWithIds.reduce(
    (acc, { id, value: keyword }) => ({ ...acc, [keyword]: id }),
    {}
  )

  return originalArray.map(keyword => ({
    id: keywordMapper[keyword] || '',
    value: keyword || '',
  }))
}

export const Keywords = ({ vendorId }: IKeywordsProps) => {
  const styles = useKeywordsStyles()
  const { userPermissions } = useUserDetails()
  const { canEditVendors } = userPermissions || {}
  const { data } = useVendorOverviewQuery({ variables: { vendorId } })
  const [editKeywords] = useEditKeywordMutation()
  const verifyKeyword = useImperativeQuery(VerifyKeywordDocument)
  const { vendor } = data || {}
  const { vendorKeywords } = vendor || {}
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [keywords, setKeywords] = useState<string[]>(
    vendorKeywords?.map(({ value }) => value) || []
  )
  const originalKeywords = vendorKeywords?.map(({ value }) => value) || []
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [vendorTaken, setVendorTaken] = useState<Pick<IVendor, 'id' | 'name'> | null>(null)

  const showRemoveKeywordDisclaimer = !containsOriginalKeywords(originalKeywords, keywords)

  const validateNewInputHandler = async (input: string): Promise<[boolean, Error | null]> => {
    if (input.length < MIN_CHARACTERS) {
      return [false, new Error(`Keyword should be at least ${MIN_CHARACTERS} characters.`)]
    }

    const { data } = await verifyKeyword({
      keyword: input,
    })
    const { searchVendorForKeyword } = data || {}
    const { vendor } = searchVendorForKeyword || {}

    if (vendor) {
      setVendorTaken(vendor)
      return [false, new Error('This keyword is taken. Try another.')]
    }
    return [true, null]
  }

  const exitEditingMode = () => {
    setKeywords(originalKeywords)
    setIsEditing(false)
  }

  const onSaveHandler = async () => {
    try {
      const keywordsInput = keywordsWithId(keywords, vendorKeywords || [])
      const { data, errors: generalErrors = [] } = await editKeywords({
        variables: {
          input: {
            id: vendorId,
            keywords: keywordsInput,
          },
        },
      })
      const { editVendorKeywords } = data || {}
      const { errors: mutationErrors = [] } = editVendorKeywords || {}
      const errors = [...generalErrors, ...mutationErrors]

      if (errors && errors.length > 0) {
        setMutationError(errors[0].message)
      } else {
        exitEditingMode()
      }
    } catch (e) {
      setMutationError(e.message)
    }
  }

  return (
    <>
      <div className={styles.editingTitle}>
        <Typography variant='subtitle1'>Mapping Keywords</Typography>
        {!isEditing && canEditVendors && (
          <Button startIcon={<EditIcon />} color='tertiary' onClick={() => setIsEditing(true)}>
            Edit Keywords
          </Button>
        )}
      </div>
      <Typography variant='body2'>
        Any keyword that matches a new incoming EDI Location Code or Shipper Facility Code name will
        automatically be mapped to this vendor. This will take effect when new shipments come.
        Keywords are not case sensitive and will match everything contained within the keyword
        phrase.
      </Typography>

      {!isEditing ? (
        <>
          <div className={styles.table}>
            <Typography className={styles.tableItem} variant='subtitle2' color='textSecondary'>
              Keywords
            </Typography>
            {originalKeywords.length > 0 ? (
              originalKeywords.map(value => (
                <Typography variant='body1' className={styles.tableItem} key={value}>
                  {value}
                </Typography>
              ))
            ) : (
              <Typography variant='body1' className={styles.tableItem}>
                {DISPLAY.empty}
              </Typography>
            )}
          </div>
        </>
      ) : null}
      {isEditing && (
        <div>
          <ChipsTextInput
            TextFieldProps={{
              label: 'Keyword',
            }}
            buttonText='Add Keyword'
            startLabels={keywords}
            onChange={async newKeywords => setKeywords(newKeywords)}
            onInputChange={() => {
              setVendorTaken(null)
            }}
            validateNewInput={validateNewInputHandler}>
            <div className={styles.messageContainer}>
              {mutationError && (
                <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={mutationError} />
              )}
              {vendorTaken && (
                <InlineMessage
                  type={INLINE_MESSAGE_VARIANTS.WARNING}
                  message={
                    <span>
                      This keyword is being used by{' '}
                      <Link
                        href={`${__ROOT__}/facilities?vendorId=${vendorTaken.id}`}
                        target='_blank'
                        rel='noopener noreferrer'>
                        {vendorTaken.name}
                      </Link>
                    </span>
                  }
                />
              )}
              {showRemoveKeywordDisclaimer && (
                <InlineMessage
                  type={INLINE_MESSAGE_VARIANTS.INFO}
                  message='Removing keywords will take effect when new shipments come in. This does not unassign vendors from any existing shipper facilities.'
                />
              )}
            </div>
          </ChipsTextInput>
          <div className={styles.editingButtonsContainer}>
            <Button color='tertiary' onClick={() => exitEditingMode()}>
              Cancel
            </Button>
            <Button color='primary' onClick={onSaveHandler}>
              Save
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
